import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useApp } from '../context/AppContext';
import {
  createDriver,
  createLocation,
  createVehicle,
  deleteDriver,
  deleteLocation,
  deleteVehicle,
  getNotificationSettings,
  updateNotificationSettings,
  updateDriver,
  updateLocation,
  updateVehicle,
} from '../services/api';

export default function Manage() {
  const { vehicles, drivers, locations, refresh } = useApp();
  const [settings, setSettings] = useState(null);
  const [vehicleForm, setVehicleForm] = useState({
    name: '',
    plate: '',
    fuelAverage: '',
    vehicleCategory: 'car',
  });
  const [driverForm, setDriverForm] = useState({ name: '', phone: '' });
  const [locationForm, setLocationForm] = useState({ name: '', fullAddress: '' });
  const [fuelAvgEditing, setFuelAvgEditing] = useState(null);
  const [fuelAvgDraft, setFuelAvgDraft] = useState('');
  const [fuelAvgSaving, setFuelAvgSaving] = useState(false);
  const [categorySavingId, setCategorySavingId] = useState(null);
  const [fuelTrackSavingId, setFuelTrackSavingId] = useState(null);

  useEffect(() => {
    getNotificationSettings().then(setSettings).catch(() => setSettings(null));
  }, []);

  const startFuelAvgUpdate = (v) => {
    setFuelAvgEditing(v._id);
    setFuelAvgDraft(v.fuelAverage ?? '');
  };

  const cancelFuelAvgUpdate = () => {
    setFuelAvgEditing(null);
    setFuelAvgDraft('');
    setFuelAvgSaving(false);
  };

  const saveFuelAvgUpdate = async (vehicleId) => {
    setFuelAvgSaving(true);
    try {
      const raw = fuelAvgDraft;
      const nextValue = raw === '' || raw == null ? null : Number(raw);
      if (nextValue !== null && !Number.isFinite(nextValue)) {
        toast.error('Fuel average must be a valid number');
        return;
      }
      await updateVehicle(vehicleId, { fuelAverage: nextValue });
      toast.success('Fuel average updated');
      await refresh();
      cancelFuelAvgUpdate();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to update fuel average');
      setFuelAvgSaving(false);
    }
  };

  const saveCategoryUpdate = async (vehicleId, vehicleCategory) => {
    setCategorySavingId(vehicleId);
    try {
      const payload = { vehicleCategory };
      if (vehicleCategory === 'bike') {
        payload.lowFuelThresholdLiters = 2;
      }
      await updateVehicle(vehicleId, payload);
      toast.success(
        vehicleCategory === 'bike'
          ? 'Category set to bike (2 L threshold)'
          : 'Vehicle category updated'
      );
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to update category');
    } finally {
      setCategorySavingId(null);
    }
  };

  const toggleTrackFuelStatus = async (vehicle) => {
    const next = vehicle.trackFuelStatus === false;
    setFuelTrackSavingId(vehicle._id);
    try {
      await updateVehicle(vehicle._id, { trackFuelStatus: next });
      toast.success(
        next
          ? `${vehicle.name} included in fuel status`
          : `${vehicle.name} excluded from fuel status (personal use)`
      );
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to update fuel tracking');
    } finally {
      setFuelTrackSavingId(null);
    }
  };

  const saveSettings = async () => {
    try {
      const updated = await updateNotificationSettings(settings);
      setSettings(updated);
      toast.success('Settings saved');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to save settings');
    }
  };

  return (
    <div>
      <h2>Manage</h2>

      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-title">Add vehicle</div>
        <div className="form-grid cols3">
          <input placeholder="Name" value={vehicleForm.name} onChange={(e) => setVehicleForm({ ...vehicleForm, name: e.target.value })} />
          <input placeholder="Plate" value={vehicleForm.plate} onChange={(e) => setVehicleForm({ ...vehicleForm, plate: e.target.value })} />
          <input placeholder="Fuel avg km/L" value={vehicleForm.fuelAverage} onChange={(e) => setVehicleForm({ ...vehicleForm, fuelAverage: e.target.value })} />
          <select
            value={vehicleForm.vehicleCategory}
            onChange={(e) => setVehicleForm({ ...vehicleForm, vehicleCategory: e.target.value })}
          >
            <option value="car">Car</option>
            <option value="bike">Bike (2 L threshold)</option>
            <option value="van">Van</option>
            <option value="truck">Truck</option>
            <option value="other">Other</option>
          </select>
        </div>
        <button className="btn btn-primary btn-sm" style={{ marginTop: 10 }} onClick={async () => {
          const category = vehicleForm.vehicleCategory || 'car';
          await createVehicle({
            name: vehicleForm.name,
            plate: vehicleForm.plate,
            fuelAverage: vehicleForm.fuelAverage ? Number(vehicleForm.fuelAverage) : null,
            vehicleCategory: category,
            lowFuelThresholdLiters: category === 'bike' ? 2 : null,
          });
          setVehicleForm({ name: '', plate: '', fuelAverage: '', vehicleCategory: 'car' });
          refresh();
          toast.success('Vehicle added');
        }}>Add vehicle</button>
        <table style={{ marginTop: 12 }}>
          <tbody>
            {vehicles.map((v) => (
              <tr key={v._id}>
                <td>{v.name}</td>
                <td>{v.plate}</td>
                <td>
                  <select
                    value={v.vehicleCategory || 'car'}
                    disabled={categorySavingId === v._id}
                    onChange={(e) => saveCategoryUpdate(v._id, e.target.value)}
                    title="Vehicle category"
                  >
                    <option value="car">Car</option>
                    <option value="bike">Bike</option>
                    <option value="van">Van</option>
                    <option value="truck">Truck</option>
                    <option value="other">Other</option>
                  </select>
                </td>
                <td>{v.fuelAverage != null ? v.fuelAverage : '—'}</td>
                <td className="text-muted">
                  {(v.vehicleCategory || 'car') === 'bike'
                    ? '≤2 L'
                    : (v.lowFuelThresholdLiters != null ? `≤${v.lowFuelThresholdLiters} L` : 'default')}
                </td>
                <td>
                  {fuelAvgEditing === v._id ? (
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <input
                        type="number"
                        step="0.01"
                        value={fuelAvgDraft}
                        onChange={(e) => setFuelAvgDraft(e.target.value)}
                        placeholder="Fuel avg km/L"
                        style={{ width: 140 }}
                        disabled={fuelAvgSaving}
                      />
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => saveFuelAvgUpdate(v._id)}
                        disabled={fuelAvgSaving}
                      >
                        {fuelAvgSaving ? 'Saving…' : 'Save'}
                      </button>
                      <button className="btn btn-sm" onClick={cancelFuelAvgUpdate} disabled={fuelAvgSaving}>
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button className="btn btn-sm" onClick={() => startFuelAvgUpdate(v)}>
                      Update avg
                    </button>
                  )}
                </td>
                <td>
                  <button
                    className="btn btn-sm"
                    disabled={fuelTrackSavingId === v._id}
                    title={
                      v.trackFuelStatus === false
                        ? 'Currently ignored — click to include in fuel status'
                        : 'Click to exclude from fuel empty/low alerts (personal use)'
                    }
                    onClick={() => toggleTrackFuelStatus(v)}
                  >
                    {fuelTrackSavingId === v._id
                      ? '…'
                      : (v.trackFuelStatus === false ? 'Fuel: ignored' : 'Fuel: tracked')}
                  </button>
                </td>
                <td>
                  <button className="btn btn-sm" onClick={() => deleteVehicle(v._id).then(refresh)}>
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-title">Add driver</div>
        <div className="form-grid cols2">
          <input placeholder="Name" value={driverForm.name} onChange={(e) => setDriverForm({ ...driverForm, name: e.target.value })} />
          <input placeholder="Phone" value={driverForm.phone} onChange={(e) => setDriverForm({ ...driverForm, phone: e.target.value })} />
        </div>
        <button className="btn btn-primary btn-sm" style={{ marginTop: 10 }} onClick={async () => {
          await createDriver(driverForm);
          setDriverForm({ name: '', phone: '' });
          refresh();
          toast.success('Driver added');
        }}>Add driver</button>
        <table style={{ marginTop: 12 }}>
          <tbody>
            {drivers.map((d) => (
              <tr key={d._id}>
                <td>{d.name}</td><td>{d.phone}</td>
                <td><button className="btn btn-sm" onClick={() => deleteDriver(d._id).then(refresh)}>Delete</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-title">Add location</div>
        <div className="form-grid cols2">
          <input placeholder="Name" value={locationForm.name} onChange={(e) => setLocationForm({ ...locationForm, name: e.target.value })} />
          <input placeholder="Full address" value={locationForm.fullAddress} onChange={(e) => setLocationForm({ ...locationForm, fullAddress: e.target.value })} />
        </div>
        <button className="btn btn-primary btn-sm" style={{ marginTop: 10 }} onClick={async () => {
          await createLocation(locationForm);
          setLocationForm({ name: '', fullAddress: '' });
          refresh();
          toast.success('Location added');
        }}>Add location</button>
        <table style={{ marginTop: 12 }}>
          <tbody>
            {locations.map((l) => (
              <tr key={l._id}>
                <td>{l.name}</td><td>{l.fullAddress}</td>
                <td><button className="btn btn-sm" onClick={() => deleteLocation(l._id).then(refresh)}>Delete</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {settings && (
        <div className="card">
          <div className="card-title">Notification settings</div>
          <div className="form-grid cols3">
            <div className="form-group"><label>Default wash interval (days)</label><input type="number" value={settings.defaultWashIntervalDays ?? 10} onChange={(e) => setSettings({ ...settings, defaultWashIntervalDays: Number(e.target.value) })} /></div>
            <div className="form-group"><label>Low fuel threshold (L) — cars / vans</label><input type="number" value={settings.lowFuelThresholdLiters ?? 10} onChange={(e) => setSettings({ ...settings, lowFuelThresholdLiters: Number(e.target.value) })} /></div>
            <div className="form-group"><label>Oil change interval (km)</label><input type="number" value={settings.oilChangeIntervalMileage ?? 5000} onChange={(e) => setSettings({ ...settings, oilChangeIntervalMileage: Number(e.target.value) })} /></div>
          </div>
          <button className="btn btn-primary" style={{ marginTop: 12 }} onClick={saveSettings}>Save settings</button>
        </div>
      )}
    </div>
  );
}
